'use client';

import { useState } from 'react';
import { Calculator, User, Activity } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';

function BodyFatCalculatorClient() {
    const [gender, setGender] = useState<'male' | 'female'>('male');
    const [age, setAge] = useState('');
    const [weight, setWeight] = useState('');
    const [height, setHeight] = useState('');
    const [neck, setNeck] = useState('');
    const [waist, setWaist] = useState('');
    const [hip, setHip] = useState('');
    const [bodyFat, setBodyFat] = useState<number | null>(null);
    const [category, setCategory] = useState('');

    const calculateBodyFat = () => {
        if (!age || !weight || !height || !neck || !waist) return;
        if (gender === 'female' && !hip) return;

        const ageNum = parseFloat(age);
        const weightNum = parseFloat(weight);
        const heightNum = parseFloat(height);
        const neckNum = parseFloat(neck);
        const waistNum = parseFloat(waist);
        const hipNum = parseFloat(hip || '0');

        let bodyFatPercentage = 0;

        if (gender === 'male') {
            // US Navy formula for males
            bodyFatPercentage = 86.010 * Math.log10(waistNum - neckNum) - 70.041 * Math.log10(heightNum) + 36.76;
        } else {
            // US Navy formula for females
            bodyFatPercentage = 163.205 * Math.log10(waistNum + hipNum - neckNum) - 97.684 * Math.log10(heightNum) - 78.387;
        }

        setBodyFat(Math.round(bodyFatPercentage * 10) / 10);

        // Determine category
        if (gender === 'male') {
            if (bodyFatPercentage < 6) setCategory('Essential fat');
            else if (bodyFatPercentage < 14) setCategory('Athletes');
            else if (bodyFatPercentage < 18) setCategory('Fitness');
            else if (bodyFatPercentage < 25) setCategory('Average');
            else setCategory('Obese');
        } else {
            if (bodyFatPercentage < 16) setCategory('Essential fat');
            else if (bodyFatPercentage < 21) setCategory('Athletes');
            else if (bodyFatPercentage < 25) setCategory('Fitness');
            else if (bodyFatPercentage < 32) setCategory('Average');
            else setCategory('Obese');
        }
    };

    const resetCalculator = () => {
        setGender('male');
        setAge('');
        setWeight('');
        setHeight('');
        setNeck('');
        setWaist('');
        setHip('');
        setBodyFat(null);
        setCategory('');
    };

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                    <Calculator className="h-8 w-8 text-blue-600" />
                </div>
                <ToolPageIntro title="Body Fat Calculator">
                Calculate your body fat percentage using the US Navy formula with accurate measurements and gender-specific calculations for optimal health tracking.
            </ToolPageIntro>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setGender('male')}
                                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${gender === 'male'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                Male
                            </button>
                            <button
                                onClick={() => setGender('female')}
                                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${gender === 'female'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                Female
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Age (years)</label>
                        <input
                            type="number"
                            value={age}
                            onChange={(e) => setAge(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter your age"
                            min="1"
                            max="120"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Weight (kg)</label>
                        <input
                            type="number"
                            value={weight}
                            onChange={(e) => setWeight(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter your weight"
                            min="1"
                            step="0.1"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Height (cm)</label>
                        <input
                            type="number"
                            value={height}
                            onChange={(e) => setHeight(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter your height"
                            min="1"
                            step="0.1"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Neck Circumference (cm)</label>
                        <input
                            type="number"
                            value={neck}
                            onChange={(e) => setNeck(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter neck measurement"
                            min="1"
                            step="0.1"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Waist Circumference (cm)</label>
                        <input
                            type="number"
                            value={waist}
                            onChange={(e) => setWaist(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter waist measurement"
                            min="1"
                            step="0.1"
                        />
                    </div>

                    {gender === 'female' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hip Circumference (cm)</label>
                            <input
                                type="number"
                                value={hip}
                                onChange={(e) => setHip(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter hip measurement"
                                min="1"
                                step="0.1"
                            />
                        </div>
                    )}
                </div>

                <div className="flex gap-4">
                    <button
                        onClick={calculateBodyFat}
                        disabled={!age || !weight || !height || !neck || !waist || (gender === 'female' && !hip)}
                        className={`flex-1 py-3 px-6 rounded-lg font-medium transition-colors ${!age || !weight || !height || !neck || !waist || (gender === 'female' && !hip)
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}
                    >
                        Calculate Body Fat
                    </button>
                    <button
                        onClick={resetCalculator}
                        className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Reset
                    </button>
                </div>
            </div>

            {bodyFat && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Results</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-blue-50 p-4 rounded-lg text-center">
                            <div className="text-3xl font-bold text-blue-600">{bodyFat}%</div>
                            <div className="text-sm text-gray-600">Body Fat Percentage</div>
                        </div>

                        <div className="bg-green-50 p-4 rounded-lg text-center">
                            <div className="text-xl font-semibold text-green-600">{category}</div>
                            <div className="text-sm text-gray-600">Category</div>
                        </div>
                    </div>

                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                        <h3 className="font-semibold text-gray-900 mb-2">Body Fat Categories ({gender === 'male' ? 'Male' : 'Female'})</h3>
                        <div className="text-sm text-gray-600 space-y-1">
                            {gender === 'male' ? (
                                <>
                                    <p>• Essential fat: 2-6%</p>
                                    <p>• Athletes: 6-14%</p>
                                    <p>• Fitness: 14-18%</p>
                                    <p>• Average: 18-25%</p>
                                    <p>• Obese: &gt;25%</p>
                                </>
                            ) : (
                                <>
                                    <p>• Essential fat: 10-16%</p>
                                    <p>• Athletes: 16-21%</p>
                                    <p>• Fitness: 21-25%</p>
                                    <p>• Average: 25-32%</p>
                                    <p>• Obese: &gt;32%</p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BodyFatCalculatorClient;
